import ls from 'localstorage-slim';
import { makeHttpRequest } from '../utils/makeHttpRequest';
import type { SplitTestConfig, SplitTestService } from './splitTestTypes';
import { SplitTestStatus } from './splitTestTypes';
import { displayDebugAlert } from '../debug/debugAlert';

const debugKey = 'SoftgamesSplitTestServiceDEBUG';

type StatusOverride = Record<string, SplitTestStatus | null>;

type DistributionOverride = Record<string, number>;

type DebugOverride = {
  status: StatusOverride | null;
  distribution: DistributionOverride | null;
};

export default class SoftgamesSplitTestService implements SplitTestService {
  private static debugOverride: DebugOverride | null = null;

  public static setDebugOverrideForTest(config: SplitTestConfig, distribution: number): void {
    const statusOverride: StatusOverride = {};
    const distributionOverride: DistributionOverride = {};

    const { newUserTestSlug, returningUserTestSlug } = config;

    if (newUserTestSlug) {
      statusOverride[newUserTestSlug] = SplitTestStatus.Active;
      distributionOverride[newUserTestSlug] = distribution;
    }

    if (returningUserTestSlug) {
      statusOverride[returningUserTestSlug] = SplitTestStatus.Active;
      distributionOverride[returningUserTestSlug] = distribution;
    }

    this.setDebugOverrides(statusOverride, distributionOverride);
  }

  public static setDebugOverrides(status: StatusOverride, distribution: DistributionOverride): void {
    ls.set<DebugOverride>(debugKey, {
      status,
      distribution,
    });
  }

  public static loadDebugOverride(): void {
    const override = ls.get<DebugOverride>(debugKey) ?? null;
    if (!override) return;

    displayDebugAlert(`SoftgamesSplitTestService is using debug override
    ${JSON.stringify(override, null, '  ')}`);
    SoftgamesSplitTestService.debugOverride = override;
  }

  public static clearDebugOverride(): void {
    ls.remove(debugKey);
  }

  constructor(
    private readonly gameSlug: string,
    private readonly playerId: string,
  ) {}

  private getStatusURL(testSlug: string): string {
    return `${this.getBaseURL(testSlug)}/status`;
  }

  private getBaseURL(testSlug: string): string {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return `https://${this.gameSlug}.st.sbs.softgames.de/gameApi/v1/splitTests/${testSlug}`;
  }

  private getDistributionURL(testSlug: string): string {
    return `${this.getBaseURL(testSlug)}/users/${this.playerId}`;
  }

  public async getDistribution(testSlug: string): Promise<number | null> {
    if (SoftgamesSplitTestService.debugOverride) {
      return SoftgamesSplitTestService.debugOverride.distribution?.[testSlug] ?? null;
    }

    try {
      const response = await makeHttpRequest(this.getDistributionURL(testSlug));
      return (JSON.parse(response) as { distributionId: number }).distributionId;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  public async getSplitTestStatus(testSlug: string): Promise<SplitTestStatus | null> {
    if (SoftgamesSplitTestService.debugOverride) {
      return SoftgamesSplitTestService.debugOverride.status?.[testSlug] ?? null;
    }

    try {
      const response = await makeHttpRequest(this.getStatusURL(testSlug));
      return (JSON.parse(response) as { status: SplitTestStatus }).status as SplitTestStatus;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }
}
